<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount, watch, computed } from "vue";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
  RadialLinearScale,
  Filler,
  type Plugin,
} from "chart.js";
import { Bar, Doughnut, Line, Pie, PolarArea } from "vue-chartjs";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
  RadialLinearScale,
  Filler
);

export interface ChartData {
  labels: string[];
  datasets: Array<{
    label?: string;
    data: number[];
    backgroundColor?: string | string[];
    borderColor?: string | string[];
    borderWidth?: number;
    fill?: boolean;
  }>;
}

export interface ChartOptions {
  responsive?: boolean;
  maintainAspectRatio?: boolean;
  plugins?: {
    legend?: {
      display?: boolean;
      position?: "top" | "left" | "bottom" | "right";
    };
    title?: {
      display?: boolean;
      text?: string;
    };
  };
  scales?: any;
}

interface Props {
  type: "bar" | "doughnut" | "line" | "pie" | "polarArea";
  data: ChartData;
  options?: ChartOptions;
  height?: number;
  /** Inline Chart.js plugins for this chart alone. Registering one globally would reach every chart. */
  plugins?: Plugin[];
}

const props = withDefaults(defineProps<Props>(), {
  height: 300,
  options: () => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "top",
      },
    },
  }),
});

const chartRef = ref();

// Chart.js defaults its text/grid to a fixed dark grey (~#666) that's near-invisible on the dark
// body. Bind those to the Bootstrap theme tokens (which flip with data-bs-theme) so legend labels,
// axis ticks, and grid lines follow the theme. Read live from the computed style rather than
// hardcoding, and re-read on theme toggle via the MutationObserver below. Only the chrome recolours
// here — dataset fill colours stay as each component sets them (light pastels read fine on dark).
const themeTick = ref(0);
const themeColors = computed(() => {
  themeTick.value; // reactive dependency — recomputes when the observer bumps it
  const styles = getComputedStyle(document.documentElement);
  const text = styles.getPropertyValue("--bs-body-color").trim() || "#212529";
  const border = styles.getPropertyValue("--bs-border-color").trim() || "rgba(0,0,0,0.1)";
  return { text, border };
});

let themeObserver: MutationObserver | null = null;

const chartComponent = computed(() => {
  switch (props.type) {
    case "bar":
      return Bar;
    case "doughnut":
      return Doughnut;
    case "line":
      return Line;
    case "pie":
      return Pie;
    case "polarArea":
      return PolarArea;
    default:
      return Bar;
  }
});

const mergedOptions = computed(() => {
  const { text, border } = themeColors.value;
  const opts: any = {
    responsive: true,
    maintainAspectRatio: false,
    ...props.options,
  };

  // Legend / title text → body colour. Merge so a component's own legend config is preserved.
  const plugins = { ...(opts.plugins ?? {}) };
  plugins.legend = {
    ...(plugins.legend ?? {}),
    labels: { color: text, ...(plugins.legend?.labels ?? {}) },
  };
  plugins.title = { color: text, ...(plugins.title ?? {}) };
  opts.plugins = plugins;

  // Axis ticks → body colour, grid lines → border colour. Applied per-scale the component declared
  // so we don't invent axes; a component's explicit ticks/grid colour still wins (spread last).
  if (opts.scales) {
    const scales: any = {};
    for (const [axis, cfg] of Object.entries(opts.scales as Record<string, any>)) {
      scales[axis] = {
        ...cfg,
        ticks: { color: text, ...(cfg?.ticks ?? {}) },
        grid: { color: border, ...(cfg?.grid ?? {}) },
      };
    }
    opts.scales = scales;
  }

  return opts;
});

watch(
  () => props.data,
  () => {
    if (chartRef.value?.chart) {
      chartRef.value.chart.update();
    }
  },
  { deep: true }
);

// Recolour on theme toggle: watching mergedOptions catches the themeColors change and pushes the
// new option values into the live chart. vue-chartjs re-reads :options reactively, but calling
// update() guarantees an immediate repaint of ticks/legend without waiting for a data change.
watch(mergedOptions, () => {
  if (chartRef.value?.chart) {
    chartRef.value.chart.update();
  }
});

onMounted(() => {
  themeObserver = new MutationObserver(() => {
    themeTick.value++; // force themeColors to re-read the (now flipped) CSS variables
  });
  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-bs-theme"],
  });
});

onBeforeUnmount(() => {
  themeObserver?.disconnect();
  themeObserver = null;
});

defineExpose({
  chart: chartRef,
});
</script>

<template>
  <div :style="{ height: `${height}px` }">
    <component
      :is="chartComponent"
      ref="chartRef"
      :data="data"
      :options="mergedOptions"
      :plugins="plugins ?? []"
    />
  </div>
</template>

<style scoped lang="scss"></style>
